import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { SERVER_URL, apiHeader } from '../../config'

interface Member {
    id: number
    firstName: string
    lastName: string
    email: string
}

interface MemberMultiSelectProps {
    selectedMemberIds: number[]
    onChange: (memberIds: number[]) => void
}

const MemberMultiSelect: React.FC<MemberMultiSelectProps> = ({ selectedMemberIds, onChange }) => {
    const { t } = useTranslation()
    const [members, setMembers] = useState<Member[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')

    // Récupérer la liste des membres
    const { data: membersData, isLoading: isLoadingMembers } = useQuery<Member[]>({
        queryKey: ['members'],
        queryFn: async () => {
            const token = localStorage.getItem('tokenCoach')
            const response = await fetch(`${SERVER_URL}/users?role=ROLE_MEMBER`, {
                ...apiHeader(token ?? ''),
            })
            if (!response.ok) {
                throw new Error('Failed to fetch members')
            }
            return response.json()
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    })

    // Mettre à jour la liste des membres lorsque les données sont chargées
    useEffect(() => {
        if (membersData) {
            setMembers(membersData)
        }
    }, [membersData])

    // Filtrer les membres en fonction du terme de recherche
    const filteredMembers = members.filter(
        (member) =>
            member.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.email?.toLowerCase().includes(searchTerm.toLowerCase())
    )

    const handleMemberToggle = (memberId: number) => {
        const newSelectedMemberIds = selectedMemberIds.includes(memberId)
            ? selectedMemberIds.filter((id) => id !== memberId)
            : [...selectedMemberIds, memberId]
        onChange(newSelectedMemberIds)
    }

    if (isLoading || isLoadingMembers) {
        return <div>{t('common.loading', 'Chargement...')}</div>
    }

    return (
        <div className="mb-3">
            <Form.Label>{t('coach.trainingSessionGroups.members', 'Membres')}</Form.Label>
            <Form.Control
                type="text"
                placeholder={t('coach.trainingSessionGroups.searchMembers', 'Rechercher des membres...')}
                className="mb-2"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div
                style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.25rem' }}
            >
                {filteredMembers.length === 0 ? (
                    <div className="p-2 text-muted">
                        {t('coach.trainingSessionGroups.noMembersFound', 'Aucun membre trouvé')}
                    </div>
                ) : (
                    <div className="list-group list-group-flush">
                        {filteredMembers.map((member) => (
                            <div
                                key={member.id}
                                className={`list-group-item list-group-item-action d-flex align-items-center ${
                                    selectedMemberIds.includes(member.id) ? 'active' : ''
                                }`}
                                onClick={() => handleMemberToggle(member.id)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="form-check me-2">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={selectedMemberIds.includes(member.id)}
                                        onChange={() => {}}
                                    />
                                </div>
                                <div>
                                    <div className="fw-bold">
                                        {member.firstName} {member.lastName}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {selectedMemberIds.length > 0 && (
                <div className="mt-2">
                    <small className="text-muted">
                        {t('coach.trainingSessionGroups.selectedMembers', { count: selectedMemberIds.length })}
                    </small>
                </div>
            )}
        </div>
    )
}

export default MemberMultiSelect
